import React, { useState } from 'react';

const BLOCKED_DOMAINS = new Set([
  'gmail.com','yahoo.com','hotmail.com','outlook.com','icloud.com',
  'aol.com','live.com','msn.com','me.com','yahoo.co.uk','hotmail.co.uk',
  'protonmail.com','proton.me','ymail.com','rocketmail.com','mail.com',
  'zoho.com','inbox.com','gmx.com','gmx.net','fastmail.com',
  'hey.com','tutanota.com','mailfence.com','yandex.com','yandex.ru',
]);

const BRAND = {
  navy:  '#0D2137',
  teal:  '#1B6B72',
  gold:  '#C9A84C',
  cream: '#F5F0E8',
};

const BULLETS = [
  'Weekly executive recognition across the Opportunity Post network',
  'Featured in Newsroom, Magazine, and Publisher\'s Signal',
  "Honoree's organization receives Partner Employer Status with Career Hub benefits",
];

const COVER_THUMB = 'https://ik.imagekit.io/enrollforge/downloads/exemplary-leaders-cover.jpg';

const overlay = {
  position: 'fixed', inset: 0, zIndex: 9999,
  background: 'rgba(13,33,55,0.72)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '16px',
  fontFamily: "'Inter', 'Manrope', sans-serif",
};

const card = {
  background: '#fff',
  borderRadius: '12px',
  overflow: 'hidden',
  display: 'flex',
  width: '100%',
  maxWidth: '860px',
  maxHeight: '90vh',
  boxShadow: '0 24px 64px rgba(13,33,55,0.28)',
};

const teaser = {
  background: BRAND.navy,
  color: '#fff',
  padding: '40px 36px',
  width: '320px',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
};

const formPanel = {
  flex: 1,
  padding: '40px 36px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: `1px solid #d0d5db`,
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'inherit',
  color: '#0D2137',
  background: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  fontSize: '12px',
  fontWeight: '600',
  color: '#ffffff',
  marginBottom: '4px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

const submitBtn = {
  width: '100%',
  padding: '12px',
  background: BRAND.teal,
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  fontSize: '15px',
  fontWeight: '600',
  fontFamily: 'inherit',
  cursor: 'pointer',
  transition: 'background 0.2s',
  letterSpacing: '0.02em',
};

export default function ExemplaryLeadersModal({ onClose }) {
  const [view, setView] = useState('teaser'); // 'teaser' | 'form' | 'success'
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', organization: '', title: '',
  });
  const [emailError, setEmailError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');

  const API = process.env.REACT_APP_BACKEND_URL;

  function handleEmailChange(e) {
    const val = e.target.value;
    setForm(f => ({ ...f, email: val }));
    const domain = val.split('@')[1]?.toLowerCase() || '';
    setEmailError(domain && BLOCKED_DOMAINS.has(domain)
      ? 'Please use a business email address'
      : '');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setServerError('');
    const domain = form.email.split('@')[1]?.toLowerCase() || '';
    if (BLOCKED_DOMAINS.has(domain)) {
      setEmailError('Please use a business email address');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/brochure-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, brochure_type: 'exemplary-leaders' }),
      });
      const data = await res.json();
      if (!res.ok) {
        setServerError(data.detail || 'Something went wrong. Please try again.');
      } else {
        setView('success');
      }
    } catch {
      setServerError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  function field(key, label, type = 'text') {
    return (
      <div key={key}>
        <label htmlFor={`el-${key}`} style={labelStyle}>{label}</label>
        <input
          id={`el-${key}`}
          data-testid={`el-${key}`}
          className="el-brochure-input"
          type={type}
          value={form[key]}
          onChange={key === 'email' ? handleEmailChange : e => setForm(f => ({ ...f, [key]: e.target.value }))}
          style={{ ...inputStyle, borderColor: key === 'email' && emailError ? '#c0392b' : '#d0d5db' }}
          required
          autoComplete="off"
        />
        {key === 'email' && emailError && (
          <p data-testid="el-email-error" style={{ color: '#c0392b', fontSize: '12px', marginTop: '4px' }}>
            {emailError}
          </p>
        )}
      </div>
    );
  }

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()} data-testid="el-modal-overlay">
      <style>{`.el-brochure-input::placeholder { color: rgba(255,255,255,0.65); }`}</style>
      <div style={card} role="dialog" aria-modal="true" aria-label="Exemplary Leaders Program">

        {/* ── Left panel (white / explanation) ── */}
        <div style={formPanel}>
          <h2 style={{ fontSize: '22px', fontWeight: '800', margin: '0 0 12px', lineHeight: 1.25, color: BRAND.navy, fontFamily: "'Manrope', sans-serif" }}>
            Exemplary Leaders Program
          </h2>

          <p style={{ fontSize: '14px', color: '#4a5e72', lineHeight: 1.65, margin: '0 0 16px' }}>
            The Exemplary Leaders Program recognizes executives making a measurable impact across industries, workforce development, education, healthcare, government, technology, and business.
          </p>

          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {BULLETS.map((b, i) => (
              <li key={i} style={{ display: 'flex', gap: '10px', fontSize: '13px', lineHeight: 1.5, color: '#4a5e72' }}>
                <span style={{ color: BRAND.teal, fontSize: '16px', flexShrink: 0, marginTop: '1px' }}>&#10003;</span>
                {b}
              </li>
            ))}
          </ul>
        </div>

        {/* ── Right panel (navy / action) ── */}
        <div style={teaser}>
          <button
            data-testid="el-close-btn"
            onClick={onClose}
            aria-label="Close"
            style={{
              alignSelf: 'flex-end', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '20px', color: 'rgba(255,255,255,0.6)', lineHeight: 1, padding: '0 2px',
            }}
          >
            &#10005;
          </button>

          {view === 'teaser' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', justifyContent: 'center', flex: 1 }}>
              <p style={{ fontSize: '14px', color: '#d6e4f0', lineHeight: 1.65, margin: 0 }}>
                If you are interested in learning more about the Exemplary Leaders award or nominating a potential recipient, request the brochure.
              </p>
              <button
                data-testid="el-teaser-request-btn"
                onClick={() => setView('form')}
                style={{ ...submitBtn, background: BRAND.gold, color: BRAND.navy }}
              >
                Request the Brochure &#8594;
              </button>
            </div>
          )}

          {view === 'form' && (
            <form onSubmit={handleSubmit} data-testid="el-brochure-form" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#ffffff', margin: 0, fontFamily: "'Manrope', sans-serif" }}>
                Request the Brochure
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {field('first_name', 'First Name')}
                {field('last_name', 'Last Name')}
              </div>
              {field('email', 'Business Email', 'email')}
              {field('organization', 'Organization')}
              {field('title', 'Title')}
              {serverError && (
                <p data-testid="el-server-error" style={{ color: '#ff8a8a', fontSize: '13px', margin: 0 }}>
                  {serverError}
                </p>
              )}
              <button
                data-testid="el-submit-btn"
                type="submit"
                disabled={submitting || !!emailError}
                style={{ ...submitBtn, opacity: submitting || emailError ? 0.6 : 1 }}
              >
                {submitting ? 'Sending…' : 'Request Access'}
              </button>
            </form>
          )}

          {view === 'success' && (
            <div data-testid="el-success-message" style={{ display: 'flex', flexDirection: 'column', gap: '16px', justifyContent: 'center', flex: 1, textAlign: 'center' }}>
              <div style={{ fontSize: '40px', color: BRAND.teal }}>&#10003;</div>
              <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#ffffff', margin: 0, fontFamily: "'Manrope', sans-serif" }}>
                Request Received
              </h3>
              <p style={{ fontSize: '14px', color: '#d6e4f0', lineHeight: 1.65, margin: 0 }}>
                Check your business email for your access link. The link expires in 48 hours.
              </p>
              <button
                data-testid="el-done-btn"
                onClick={onClose}
                style={{ ...submitBtn, width: 'auto', alignSelf: 'center', background: BRAND.teal }}
              >
                Done
              </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
